import type {
  ProcessedTshirtImage,
  SegmentationDiagnostics,
  SegmentationSource,
} from "../../../shared/image-processing";
import type { TshirtSide } from "../../../shared/tshirts";

type PendingTshirtImageRecord = {
  readonly id: "pending-tshirt-image";
  readonly side: TshirtSide;
  readonly blobBytes: ArrayBuffer;
  readonly blobType: string;
  readonly width: number;
  readonly height: number;
  readonly segmentationSource: SegmentationSource;
  readonly segmentationOpaqueRatio: number;
  readonly segmentationDiagnostics: SegmentationDiagnostics;
  readonly createdAt: string;
};

const databaseName = "tshirt-memory-pending-save";
const databaseVersion = 1;
const storeName = "pending-images";
const pendingImageId = "pending-tshirt-image";

const openPendingSaveDatabase = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, databaseVersion);

    request.onupgradeneeded = () => {
      request.result.objectStoreNames.contains(storeName)
        ? null
        : request.result.createObjectStore(storeName, {
            keyPath: "id",
          });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDBを開けませんでした"));
  });

const runTransaction = async <TResult>(params: {
  readonly mode: IDBTransactionMode;
  readonly run: (store: IDBObjectStore) => Promise<TResult>;
}): Promise<TResult> => {
  const database = await openPendingSaveDatabase();

  return params
    .run(database.transaction(storeName, params.mode).objectStore(storeName))
    .finally(() => database.close());
};

const requestToPromise = <TResult>(
  request: IDBRequest<TResult>,
): Promise<TResult> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB操作に失敗しました"));
  });

const readBlobWithFileReader = (blob: Blob): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () =>
      reader.result instanceof ArrayBuffer
        ? resolve(reader.result)
        : reject(new Error("BlobをArrayBufferへ変換できませんでした"));
    reader.onerror = () =>
      reject(reader.error ?? new Error("Blobの読み込みに失敗しました"));
    reader.readAsArrayBuffer(blob);
  });

export const convertBlobToArrayBuffer = (blob: Blob): Promise<ArrayBuffer> =>
  "arrayBuffer" in blob && typeof blob.arrayBuffer === "function"
    ? blob.arrayBuffer()
    : readBlobWithFileReader(blob);

export const createPendingRecord = async (
  image: ProcessedTshirtImage,
): Promise<PendingTshirtImageRecord> => ({
  id: pendingImageId,
  side: image.side,
  blobBytes: await convertBlobToArrayBuffer(image.blob),
  blobType: image.blob.type || "image/webp",
  width: image.width,
  height: image.height,
  segmentationSource: image.segmentationSource,
  segmentationOpaqueRatio: image.segmentationOpaqueRatio,
  segmentationDiagnostics: image.segmentationDiagnostics,
  createdAt: new Date().toISOString(),
});

export const createProcessedImage = (
  record: PendingTshirtImageRecord,
): ProcessedTshirtImage => {
  const blob = new Blob([record.blobBytes], { type: record.blobType });

  return {
    side: record.side,
    blob,
    width: record.width,
    height: record.height,
    objectUrl: URL.createObjectURL(blob),
    segmentationSource: record.segmentationSource,
    segmentationOpaqueRatio: record.segmentationOpaqueRatio,
    segmentationDiagnostics: record.segmentationDiagnostics,
  };
};

export const isPendingRecord = (
  value: unknown,
): value is PendingTshirtImageRecord =>
  value !== null &&
  typeof value === "object" &&
  "id" in value &&
  "blobBytes" in value &&
  "blobType" in value &&
  "side" in value &&
  "width" in value &&
  "height" in value &&
  value.id === pendingImageId &&
  value.blobBytes instanceof ArrayBuffer &&
  typeof value.blobType === "string" &&
  (value.side === "front" || value.side === "back") &&
  typeof value.width === "number" &&
  typeof value.height === "number";

export const savePendingTshirtImage = async (
  image: ProcessedTshirtImage,
): Promise<void> =>
  createPendingRecord(image).then((record) =>
    runTransaction({
      mode: "readwrite",
      run: (store) => requestToPromise(store.put(record)),
    }).then(() => undefined),
  );

export const readPendingTshirtImage =
  async (): Promise<ProcessedTshirtImage | null> =>
    runTransaction({
      mode: "readonly",
      run: (store) => requestToPromise(store.get(pendingImageId)),
    }).then((record) =>
      isPendingRecord(record) ? createProcessedImage(record) : null,
    );

export const clearPendingTshirtImage = async (): Promise<void> =>
  runTransaction({
    mode: "readwrite",
    run: (store) => requestToPromise(store.delete(pendingImageId)),
  }).then(() => undefined);
